import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { createRef, useEffect, useMemo, useRef, useState } from "react";
import ConfettiExplosion from "react-confetti-explosion";
import { FormattedMessage } from "react-intl";
import { useHistory, useLocation } from "react-router";

import CardBase from "@components/cards/CardBase";
import styles from "@components/carePlan/CarePlan.module.css";
import Goal from "@components/carePlan/Goal";
import ExpertAvatar from "@components/ExpertAvatar";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading1 from "@components/typography/Heading1";
import Heading2 from "@components/typography/Heading2";
import useMyClient from "@hooks/useMyClient";
import { CarePlanSectionEnum } from "@typing/Enums";
import { GetJourneyWithPlanQuery, GoalStatusEnum } from "@typing/Generated";
import { Direction } from "@typing/Models";
import { sortByPosition } from "@utils/modelUtils";
import { isMobile } from "@utils/platformUtils";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  completed: boolean;
  goalId?: string | null;
  handleCurrentSectionChange: (section: CarePlanSectionEnum, direction: Direction, worksheetId?: string) => void;
  journey: GetJourneyWithPlanQuery["journey"];
};

interface GoalRef {
  [key: string]: React.RefObject<HTMLDivElement>;
}

const Goals = ({ completed, goalId, handleCurrentSectionChange, journey }: Props) => {
  const myClient = useMyClient();
  const history = useHistory();
  const location = useLocation();

  const goalRefs = useRef<GoalRef>({});

  const goals = useMemo(() => {
    const goals = sortByPosition(
      journey.goals
        .filter(
          goal =>
            goal.completed === completed ||
            (completed &&
              !goal.completed &&
              (goal.carePlanActionItems.some(ai => ai.completed) ||
                goal.tasks.some(t => t.clientCompleted) ||
                goal.worksheets.some(w => w.completed) ||
                goal.goalItems.some(gi => gi.completed)))
        )
        .filter(goal => goal.status !== GoalStatusEnum.DRAFT)
        .filter(g => !g.archived)
    );
    if (journey.carePlanPublishedAt) {
      return goals;
    } else {
      return goals.filter(goal => goal.author.__typename !== "Expert");
    }
  }, [completed, journey.carePlanPublishedAt, journey.goals]);

  goals.forEach(goal => {
    if (!goalRefs.current[goal.id]) {
      goalRefs.current[goal.id] = createRef();
    }
  });

  const [isExploding, setIsExploding] = useState(false);

  useEffect(() => {
    if (goalId && goalRefs.current[goalId]) {
      setTimeout(() => {
        const queryParams = new URLSearchParams(window.location.search);
        goalRefs.current[goalId].current?.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });

        // remove carePlanGoalId from query params
        queryParams.delete("carePlanGoalId");
        history.replace({
          hash: location.hash,
          pathname: location.pathname,
          search: queryParams.toString()
        });
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalId]);

  return (
    <div className={`padding-40-bottom ${styles.mobileTopSpacing}`}>
      {isExploding && (
        <ConfettiExplosion
          colors={["#5f49d3", "#82d4e8", "#ff7673", "#FAC7F3", "#F7DBF4"]}
          duration={2500}
          force={0.6}
          particleCount={85}
          width={1000}
        />
      )}
      {goals.length > 0 ? (
        <>
          {goals.map(goal => (
            <div key={goal.id} ref={goalRefs.current[goal.id]}>
              <Goal
                completed={completed}
                goal={goal}
                handleCurrentSectionChange={handleCurrentSectionChange}
                journey={journey}
                key={goal.id}
                setIsExploding={setIsExploding}
              />
            </div>
          ))}
          <div className={styles.goalActions}>
            <ButtonLink
              className={`flex justify-between items-center full-width ${styles.sectionButton}`}
              onClick={() =>
                isMobile()
                  ? handleCurrentSectionChange(CarePlanSectionEnum.GOAL_LIBRARY, "right")
                  : history.push(
                      journeyRoute({ journeyId: journey.id, linkAfterJourney: "/care-plan/goal-templates", myClient })
                    )
              }
              stylized={false}
            >
              <div className="flex items-center gap-lg">
                <Icon color="primary" icon="add" />
                <Heading2 color="primary">
                  <FormattedMessage id="components.Goals.add.title" />
                </Heading2>
              </div>
              <div className="flex items-center gap-md">
                <BodyCopy bold color="gray" size="small">
                  <FormattedMessage id="components.Goals.add.subtitle" />
                </BodyCopy>
                <Icon color="gray" icon="chevronRight" size="medium" />
              </div>
            </ButtonLink>

            {journey.goals.some(goal => goal.archived) && (
              <ButtonLink
                className={`flex justify-between items-center full-width ${styles.sectionButton}`}
                onClick={() => handleCurrentSectionChange(CarePlanSectionEnum.ARCHIVE, "right")}
                stylized={false}
              >
                <div className="flex items-center gap-lg">
                  <Icon color="primary" icon="boxArchive" />
                  <Heading2 color="primary">
                    <FormattedMessage id="components.Goals.archive.title" />
                  </Heading2>
                </div>
                <div className="flex items-center gap-md">
                  <BodyCopy bold color="gray" size="small">
                    <FormattedMessage id="components.Goals.archive.subtitle" />
                  </BodyCopy>
                  <Icon color="gray" icon="chevronRight" size="medium" />
                </div>
              </ButtonLink>
            )}
          </div>
        </>
      ) : (
        !journey.carePlanPublishedAt && (
          <CardBase
            body={
              <>
                <div className="align-center margin-40-top margin-40-bottom">
                  <Heading1>
                    <FormattedMessage id="components.Goals.empty.title" />
                  </Heading1>
                  <div>
                    <BodyCopy color="gray">
                      <FormattedMessage id="components.Goals.empty.subtitle" />
                    </BodyCopy>
                  </div>
                </div>
                <IonGrid>
                  <IonRow>
                    <IonCol className="padding-20-start padding-20-end" size="12" sizeLg="6" sizeXl="6">
                      <CardBase
                        body={
                          <div className="flex flex-column gap-md items-center text-center">
                            <Icon color="primary" icon="add" size="xlarge" />
                            <div>
                              <Heading2 bold>
                                <FormattedMessage id="components.Goals.empty.ctas.set.title" />
                              </Heading2>
                              <BodyCopy color="gray">
                                <FormattedMessage id="components.Goals.empty.ctas.set.subtitle" />
                              </BodyCopy>
                            </div>
                          </div>
                        }
                        border={false}
                        className="full-height"
                        color="primary-50"
                        contentClassName="padding-20"
                        onClick={() => handleCurrentSectionChange(CarePlanSectionEnum.GOAL_LIBRARY, "right")}
                        routerLink={
                          isMobile()
                            ? undefined
                            : journeyRoute({
                                journeyId: journey.id,
                                linkAfterJourney: "/care-plan/goal-templates",
                                myClient
                              })
                        }
                      />
                    </IonCol>
                    <IonCol className="padding-20-start padding-20-end" size="12" sizeLg="6" sizeXl="6">
                      <CardBase
                        body={
                          <div className="flex flex-column gap-md items-center text-center">
                            {journey.primaryExpert && <ExpertAvatar expert={journey.primaryExpert} size={36} />}
                            <div>
                              <Heading2 bold>
                                <FormattedMessage id="components.Goals.empty.ctas.support.title" />
                              </Heading2>
                              <BodyCopy color="gray">
                                <FormattedMessage
                                  id="components.Goals.empty.ctas.support.subtitle"
                                  values={{ expertName: journey.primaryExpert?.firstName }}
                                />
                              </BodyCopy>
                            </div>
                          </div>
                        }
                        border={false}
                        className="full-height"
                        color="primary-50"
                        contentClassName="padding-20"
                        routerLink={journeyRoute({
                          journeyId: journey.id,
                          linkAfterJourney: "/schedule-consult",
                          myClient
                        })}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </>
            }
            contentClassName="padding-40"
            rounding="large"
          />
        )
      )}
    </div>
  );
};

export default Goals;
